import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  getAllCountriesList,
  getSelectedExhibitionId,
  getSelectedStep,
  getSelfRegistration,
  getStepsValidity,
  isEventSeriesPage
} from '@app/app.reducer';
import { ZipCodeValidationResponse } from '@core/data-access/models/api/zip-code.model';
import { BaseRoute } from '@core/data-access/models/routes.model';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BookingAmanoParkingReservation } from '@products/models/booking.model';
import { InputBase } from '@shared/forms/inputs/input-base.class';
import { environment } from '@src/environments/environment';
import { SetActiveLanguage, SetSpinnerValue, SetSupportedLanguages } from '@store/helpers/helper.actions';
import {
  CountryListResponse,
  Hash,
  HashValidityStatusResponse,
  QuestionnaireInputResponseModel
} from '@store/helpers/helper.interface';
import { getClaimedTicketHash } from '@store/products/booking/booking.selectors';
import { getAllBookedAmanoParkingReservations } from '@store/products/booking/product/amano-parking/amano-parking-booking.selectors';
import { StepName } from '@store/step-forms/step.interface';
import { mapStepNameToStepRoute } from '@store/step-forms/utils/step-validity-mappers';
import { getParser } from 'bowser';
import { saveAs } from 'file-saver';
import { isEmpty } from 'lodash';
import { Observable, Subscriber, combineLatest, filter, first, map, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  listOfAllCountries: Array<string>;
  buyerQuestionnaire: InputBase<any>[] = [];
  visitorQuestionnaire: InputBase<any>[] = [];

  private isReloadSections = false;
  private emailRequired = false;
  private verifyEmailRequired = false;

  isSelfRegistration = this.store.selectSignal(getSelfRegistration);

  constructor() {
    this.store.pipe(select(getAllCountriesList)).subscribe((list) => (this.listOfAllCountries = list));
    this.isMobile();
  }

  setLanguage(lang: string) {
    this.store.dispatch(new SetActiveLanguage(lang));
  }

  setSupportedLanguages(languages: string[]) {
    this.translateService.addLangs(languages);
    this.store.dispatch(new SetSupportedLanguages(languages));
  }

  redirectAfterLogin() {
    //get selected event ID and its step, if both present go to last selected step after login, otherwise go to homepage
    combineLatest([
      this.store.pipe(select(getSelectedExhibitionId)),
      this.store.pipe(select(getSelectedStep)),
      this.store.pipe(select(isEventSeriesPage)),
      this.store.pipe(select(getStepsValidity)),
      this.store.pipe(select(getClaimedTicketHash))
    ])
      .pipe(first())
      .subscribe(([selectedExhibitionId, selectedStep, eventSeriesPage, validations, ticketClaimedHash]) => {
        const visibleSteps = Object.keys(validations)
          // make sure steps are ordered correctly
          .sort((a, b) => validations[a].order - validations[b].order)
          // only navigate to visible routes
          .filter((stepKey) => validations[stepKey].visible);

        if (selectedExhibitionId !== null && selectedStep !== null) {
          const queryString = this.router.url.includes('?');
          const baseRoute = this.isSelfRegistration() ? BaseRoute.SelfRegistration : BaseRoute.WebShop;

          if (selectedStep === StepName.Payment) {
            console.log(
              '%c currentStep ',
              'background: #d46a6a; color: #fff',
              `/${baseRoute}/${selectedExhibitionId}/${selectedStep}`
            );
          }

          let selectedStepRoute = '';

          if (!validations[selectedStep].showInStepNavigation) {
            const ticketStep = visibleSteps.find((step) => step === StepName.Tickets);
            selectedStepRoute = validations[ticketStep].showInStepNavigation
              ? ticketStep
              : validations[visibleSteps[0]].showInStepNavigation
                ? visibleSteps[0]
                : StepName.Tickets;

            console.log(
              '%c re-navigated URL ',
              'background: #6ba099; color: #fff',
              `/${baseRoute}/${selectedExhibitionId}/${selectedStep}`
            );
          }

          selectedStepRoute = mapStepNameToStepRoute(selectedStep);

          if (queryString) {
            // Bug 3830 - if CONTINUE is pressed for the first time on personalization step, user is redirected to tickets step instead of confirmation step
            this.activatedRoute.queryParams.pipe(first()).subscribe((params) => {
              this.router.navigate([`${baseRoute}/${selectedExhibitionId}/${selectedStepRoute}`], {
                queryParams: params
              });
            });
          } else {
            this.router.navigate([`${baseRoute}/${selectedExhibitionId}/${selectedStepRoute}`]);
          }
        } else if (!!eventSeriesPage && eventSeriesPage.isEventSeries) {
          this.router.navigate([`/${BaseRoute.Series}/${eventSeriesPage.eventSeriesId}`]);
        } else if (ticketClaimedHash) {
          this.router.navigate([`${BaseRoute.WebShopDownload}/${selectedExhibitionId}`], {
            queryParams: { t: ticketClaimedHash }
          });
        } else {
          this.router.navigate(['/']);
        }
      });
  }

  getListOfCountries() {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/country/preferred`);
  }

  loadCountries() {
    return this.http.get<CountryListResponse>(`${environment.protocol}${environment.webApiUrl}/country/list`);
  }

  checkZipcodeValidity(countryCode: string, zipCode: string) {
    if (!zipCode) {
      return new Observable<ZipCodeValidationResponse>((subscriber: Subscriber<any>) =>
        subscriber.next({ isValidZipCode: true })
      );
    }

    return this.http.get<ZipCodeValidationResponse>(
      `${environment.protocol}${environment.webApiUrl}/country/${countryCode}/check-zip-code/${zipCode}`
    );
  }

  checkHashValidity(hash: Hash) {
    return this.http.get<HashValidityStatusResponse>(
      `${environment.protocol}${environment.webApiUrl}/hash/status/${hash}`
    );
  }

  convertDate(strDate) {
    // strDate = '03.09.1979';
    const dateParts = strDate.split('.');
    const newDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
    return newDate;
  }

  toStringWithoutOffset(date: Date) {
    return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}T${(
      '0' + date.getHours()
    ).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}`;
  }

  processFormValuesBeforeSave(formValues: any) {
    const normalizedData = {};

    Object.keys(formValues).forEach((groupKey) => {
      normalizedData[groupKey] = formValues[groupKey];
      const typeOf = typeof formValues[groupKey];

      // if it is an object we are dealing with checkbox set
      if (typeOf === 'object') {
        const groupRegex = new RegExp(`^${groupKey}_`);
        const isDateOfBirth = groupKey === 'dateOfBirth';

        if (isDateOfBirth) {
          const dateOfBirth = formValues[groupKey];

          if (dateOfBirth) {
            formValues[groupKey] = this.getUTCdate(dateOfBirth);
            normalizedData[groupKey] = formValues[groupKey];
          }
        } else {
          Object.keys(formValues[groupKey]).forEach((checkboxId) => {
            const clearedId = checkboxId.replace(groupRegex, '');
            normalizedData[clearedId] = formValues[groupKey][checkboxId];
          });
        }
      } else if (typeOf === 'string') {
        //sanitize the input value (it should already come here sanitized anyway):
        const value = this.sanitizeString(formValues[groupKey]);

        if (value !== formValues[groupKey]) {
          formValues[groupKey] = value;
          normalizedData[groupKey] = value;
        }
      }
    });

    return normalizedData;
  }

  processQuestionnaireValuesBeforeSave(formControls: InputBase<any>[]): Array<QuestionnaireInputResponseModel> {
    const normalizedQuestionnaire = [];

    if (isEmpty(formControls)) {
      return normalizedQuestionnaire;
    }

    formControls.forEach((group) => {
      if (
        (group.controlType === 'dropdown' || group.controlType === 'radio') &&
        Object.hasOwn(group, 'value') &&
        !group.hidden &&
        (group.value || group.value === 0)
      ) {
        normalizedQuestionnaire.push({
          fieldId: Number(group.key),
          valueId: Number(group.value),
          text: null
        });
      } else if (group.controlType === 'textbox' && Object.hasOwn(group, 'value') && !group.hidden && group.value) {
        const keySplited = group.key.split('_');
        normalizedQuestionnaire.push({
          fieldId: Number(keySplited[0]),
          valueId: Number(keySplited[1]),
          text: group.value
        });
      } else if (group.controlType === 'checkbox' && !group.hidden) {
        group.options.forEach((option) => {
          if (option.value) {
            normalizedQuestionnaire.push({
              fieldId: Number(group.key),
              valueId: Number(option.key),
              text: null
            });
          }
        });
      }
    });

    return normalizedQuestionnaire;
  }

  isNumber(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  getVoucherUrl(): string {
    let voucherURL: string;

    this.activatedRoute.queryParams.pipe(first()).subscribe((params) => {
      Object.keys(params).find((key) => {
        if (key === 'voucher') {
          return (voucherURL = params.voucher);
        }
      });
    });

    return voucherURL ? voucherURL : '';
  }

  getTicketParams(): string {
    let ticketParams: string[] = [];

    this.activatedRoute.queryParams.pipe(first()).subscribe((params) => {
      let counter = 0;

      ticketParams = Object.keys(params).map((key) => {
        if (key === 'tt' || key === 'pt' || key === 'amt') {
          return (counter++ === 0 ? '?' : '&') + `${key}=${params[key]}`;
        }
      });
    });

    return ticketParams.length > 1 ? ticketParams.join('') : null;
  }

  isMobile() {
    if (typeof navigator !== 'undefined') {
      return navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i) && true;
    }

    return false;
  }

  isMobile$(): Observable<boolean> {
    return of(this.isMobile());
  }

  browserInfo() {
    const browserInfo = getParser(window.navigator.userAgent)['parsedResult'];
    const browserName = browserInfo.browser.name + ' version: ' + browserInfo.browser.version;
    const systemInfo = browserInfo.os.name + ' version: ' + browserInfo.os.versionName;
    const platformInfo = browserInfo.platform.type;
    const completeInfo = 'Browser: ' + browserName + ', SystemInfo: ' + systemInfo + ', Platform: ' + platformInfo;

    return completeInfo;
  }

  triggerCallbackOnceFormValidationIsDone(
    form: UntypedFormGroup,
    callback: () => void,
    isUsedWithoutTimeout?: boolean
  ) {
    if (form.pending) {
      const interval = setInterval(() => {
        if (!form.pending) {
          callback();
          clearInterval(interval);
        }
      }, 200);
    } else {
      if (isUsedWithoutTimeout) {
        callback();
      } else {
        setTimeout(() => {
          callback();
        }, 200);
      }
    }
  }

  isEventSeriesPage() {
    let isEventSeries = false;

    this.store
      .pipe(select(isEventSeriesPage), first())
      .subscribe((eventSeries) => (isEventSeries = eventSeries.isEventSeries));

    return isEventSeries;
  }

  saveToFileSystem(blob: string | Blob, name: string) {
    saveAs(blob, name);
    this.store.dispatch(new SetSpinnerValue(false));
  }

  openIframe(url: string) {
    const win = window.open(url, '_blank');
    win.focus();
  }

  removeQueryParam(key: string, sourceURL: string) {
    const queryString = sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : '';

    let rtn = sourceURL.split('?')[0];
    let param;
    let params_arr = [];

    if (queryString !== '') {
      params_arr = queryString.split('&');

      for (let i = params_arr.length - 1; i >= 0; i -= 1) {
        param = params_arr[i].split('=')[0];

        if (param === key) {
          params_arr.splice(i, 1);
        }
      }

      if (params_arr.length > 0) {
        rtn = rtn + '?';
      }

      rtn = rtn + params_arr.join('&');
    }

    return rtn;
  }

  applyFullHeightToAllParentElements(startingElement: HTMLElement, displayBlock?: boolean) {
    let parentElement = startingElement.parentElement;

    while (parentElement) {
      parentElement.style.height = '100%';

      if (displayBlock) {
        parentElement.style.display = 'block';
      }

      parentElement = parentElement.parentElement;
    }
  }

  getAmanoParkingReservations(): BookingAmanoParkingReservation[] {
    let storedAmanoParkingReservations: BookingAmanoParkingReservation[] = [];

    this.store
      .pipe(
        select(getAllBookedAmanoParkingReservations),
        first((amanoParkingReservations) => !!amanoParkingReservations.length)
      )
      .subscribe((amanoParkingReservations) => (storedAmanoParkingReservations = amanoParkingReservations));

    return storedAmanoParkingReservations;
  }

  //date time functions:
  //TODO: consider using moment.js
  getUTCdate(date: Date): Date {
    if (date) {
      if (typeof date === 'string' || date instanceof String) {
        date = new Date(date);
      }

      if (date instanceof Date) {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
      }
    }
    return null;
  }

  /**
   * Removes the time part of a date object.
   * @param date
   * @returns Date object without time part.
   */
  truncateTime(date: Date): Date {
    if (!!date && date instanceof Date) {
      date.setHours(0, 0, 0, 0);
      return date;
    }

    return date;
  }

  /**
   * Checks if both dates without their time part are the same.
   * @param date1
   * @param date2
   * @returns True if both dates are the same.
   */
  areDatesSameWOTime(date1: Date, date2: Date): boolean {
    if (!date1 && !date2) {
      return true;
    }

    if (!!date1 && !!date2 && this.truncateTime(date1).valueOf() === this.truncateTime(date2).valueOf()) {
      return true;
    }

    return false;
  }

  /**
   * Checks if the first date is greater than the second date.
   * @param date1
   * @param date2
   * @returns True if the first date is greater than the second one.
   */
  isFirstDateGreaterWOTime(date1: Date, date2: Date): boolean {
    if (!!date1 && !date2) {
      return true;
    }

    if (!!date1 && !!date2 && this.truncateTime(date1).valueOf() > this.truncateTime(date2).valueOf()) {
      return true;
    }

    return false;
  }

  setIsReloadSections(isReloadSections: boolean) {
    this.isReloadSections = isReloadSections;
  }

  getReloadSections(): boolean {
    return this.isReloadSections;
  }

  /**
   *
   * @param values array to sort
   * @returns array sorted by values ascending
   */
  sortValuesAsc(values) {
    values.sort(function (a, b) {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });
    return values;
  }

  setOriginalEmailValues(emailRequired: boolean, verifyEmailRequired: boolean) {
    this.emailRequired = emailRequired;
    this.verifyEmailRequired = verifyEmailRequired;
  }

  getOriginalEmailValues(value: string): boolean {
    if (value === 'email') {
      return this.emailRequired;
    } else if (value === 'verifyEmail') {
      return this.verifyEmailRequired;
    }

    return false;
  }

  sanitizeInput(control: AbstractControl, $event: InputEvent | any) {
    if ($event && typeof $event === 'object' && $event?.target?.value.includes('>')) {
      const value = this.sanitizeString($event.target.value);

      if ($event.target.value !== value) {
        $event.target.value = value;

        if (control) {
          control.setValue(value);
        }
      }
    }
  }

  sanitizeString(value: string) {
    // DomSanitizer.sanitize also escapes characters which isn't necessary here so I won't use it:
    // return this.sanitizer.sanitize(SecurityContext.HTML, value);

    return value ? value.replace(/<[^>]+>/g, '') : value;
  }

  isReloaded$() {
    const [navigationEntry] = performance.getEntriesByType('navigation');
    const isReloaded = (navigationEntry as PerformanceNavigationTiming).type === 'reload';

    return of(isReloaded);
  }

  isSelectExhibitionPage$() {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).url === '/')
    );
  }
}
